import { useCallback, useState } from "react";
import { FunctionsHttpError } from "@supabase/supabase-js";
import { supabase } from "@/lib/supabase";

export interface CopyResult {
  titulo: string;
  legenda: string;
  hashtags: string[];
  cta: string;
  // A Edge Function ai-copy devolve este campo em camelCase (serializa o
  // CopyResult de supabase/functions/_shared/gemini.ts, ja mapeado de
  // mensagem_completa pra mensagemCompleta na normalizacao da resposta do
  // Gemini). snake_case aqui era um type mismatch silencioso - o campo vinha
  // sempre undefined em runtime.
  mensagemCompleta: string;
}

export interface GenerateCopyInput {
  productUrl: string;
  affiliateUrl: string;
  marketplaceLabel?: string;
  productName?: string;
  price?: number;
}

interface InvokeSuccess {
  data: CopyResult;
}

interface InvokeErrorBody {
  error?: string;
}

const ERROR_LABELS: Record<string, string> = {
  invalid_product_url: "Informe uma URL de produto válida.",
  tenant_not_found: "Tenant ativo não encontrado.",
  gemini_key_missing: "Configure sua chave Gemini em Minha conta para gerar copy no plano Free.",
  gemini_key_invalid: "Sua chave Gemini parece inválida. Atualize-a em Minha conta.",
  platform_key_missing: "Geração de copy temporariamente indisponível. Tente novamente em instantes.",
  platform_daily_cap_exceeded: "Limite diário de gerações atingido. Tente novamente amanhã.",
  gemini_rate_limited: "O Gemini limitou as chamadas agora. Tente novamente em alguns minutos.",
  gemini_invalid_response: "O Gemini respondeu em um formato inesperado. Tente novamente.",
  gemini_upstream_error: "Erro ao falar com o Gemini agora. Tente novamente em instantes.",
};

const DEFAULT_ERROR = "Não foi possível gerar a copy agora. Tente novamente.";

async function readErrorCode(invokeError: unknown): Promise<string | null> {
  if (!(invokeError instanceof FunctionsHttpError)) {
    return null;
  }
  try {
    const body = (await invokeError.context.json()) as InvokeErrorBody;
    return body.error ?? null;
  } catch {
    return null;
  }
}

export interface UseGenerateCopyResult {
  generateCopy: (input: GenerateCopyInput) => Promise<CopyResult | null>;
  loading: boolean;
  error: string | null;
}

/** Hook de geracao de copy de afiliado via IA (Modulo 2 de paridade).
 *  Chama a Edge Function ai-copy, que resolve a chave Gemini (BYOK no Free,
 *  chave da plataforma no Pro/Pro+IA) e devolve o JSON estruturado. */
export function useGenerateCopy(): UseGenerateCopyResult {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const generateCopy = useCallback(async (input: GenerateCopyInput): Promise<CopyResult | null> => {
    setLoading(true);
    setError(null);

    const { data, error: invokeError } = await supabase.functions.invoke<InvokeSuccess>("ai-copy", {
      body: input,
    });

    if (invokeError || !data?.data) {
      const code = await readErrorCode(invokeError);
      setError((code && ERROR_LABELS[code]) || DEFAULT_ERROR);
      setLoading(false);
      return null;
    }

    setLoading(false);
    return data.data;
  }, []);

  return { generateCopy, loading, error };
}
